/**
 * Language utility functions for detecting and validating language codes
 */

const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'de', 'pt'];

/**
 * Get language from URL path
 * @param {string} pathname - The pathname to check
 * @returns {string|null} - Language code or null
 */
export const getLanguageFromPath = (pathname) => {
  if (!pathname) return null;
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  return SUPPORTED_LANGUAGES.includes(firstSegment) ? firstSegment : null;
};

/**
 * Validate language code
 * @param {string} langCode - Language code to validate
 * @returns {string} - Valid language code or 'en' as fallback
 */
export const validateLanguageCode = (langCode) => {
  if (!langCode) return 'en';
  const normalized = langCode.toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : 'en';
};

/**
 * Get current language from multiple sources (priority order)
 * 1. URL path
 * 2. localStorage preferred_locale (from livelovespain.com)
 * 3. localStorage selectedLanguage
 * 4. Browser language
 * 5. Default to 'en'
 * @param {string} pathname - Current pathname
 * @returns {string} - Detected language code
 */
export const getCurrentLanguage = (pathname) => {
  // Check URL path first
  const urlLang = getLanguageFromPath(pathname);
  if (urlLang) return urlLang;

  // Check localStorage for preferred_locale (from main site)
  if (typeof window !== 'undefined') {
    const preferredLocale = localStorage.getItem('preferred_locale');
    if (preferredLocale && SUPPORTED_LANGUAGES.includes(preferredLocale)) {
      return preferredLocale;
    }

    // Check selectedLanguage
    const selectedLanguage = localStorage.getItem('selectedLanguage');
    if (selectedLanguage && SUPPORTED_LANGUAGES.includes(selectedLanguage)) {
      return selectedLanguage;
    }

    // Browser language detection
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && SUPPORTED_LANGUAGES.includes(browserLang)) {
      return browserLang;
    }
  }

  return 'en';
};

/**
 * Get all supported languages
 * @returns {string[]} - Array of supported language codes
 */
export const getSupportedLanguages = () => {
  return [...SUPPORTED_LANGUAGES];
};

/**
 * Remove language prefix from path
 * @param {string} pathname - The pathname to clean
 * @returns {string} - Path without language prefix
 */
export const removeLanguagePrefix = (pathname) => {
  if (!pathname) return '/';
  return pathname.replace(/^\/(en|es|fr|de|pt)(\/|$)/, '/') || '/';
};

/**
 * Create a localized URL with language prefix
 * @param {string} path - The path to localize (e.g., '/crm', '/contact')
 * @param {string} lang - Language code (e.g., 'en', 'es', 'fr')
 * @returns {string} - Localized path (e.g., '/fr/crm' or '/crm' for English)
 */
export const createLocalizedUrl = (path, lang) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // Remove any existing language prefix
  const cleanPath = removeLanguagePrefix(normalizedPath);
  
  // For English, don't add prefix (or add it if you want /en prefix)
  if (lang === 'en') {
    return cleanPath;
  }
  
  // For other languages, add prefix
  return `/${lang}${cleanPath}`;
};

